import {
  ArrowDownTrayIcon,
  DocumentIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  AttachmentCapabilities,
  AttachmentFile,
  AttachmentTarget,
  AttachmentTypeCode,
  extractErrorMessage,
} from "@ots-frontend/ots-data";
import {useState} from "react";
import {OtsActionButton, OtsAsyncActionButton, OtsIconActionButton} from "../components/OtsButton";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {attachmentDate, attachmentLabel, formatAttachmentSize} from "./attachment-utils";

export type AttachmentListProps = {
  attachments: AttachmentFile[];
  targets: AttachmentTarget[];
  capabilities: AttachmentCapabilities;
  attachmentTypes: AttachmentTypeCode[];
  onUpdate?: (attachment: AttachmentFile, changes: Pick<AttachmentFile, "attachmentType" | "description" | "displayOrder">) => Promise<void> | void;
  onDelete?: (attachment: AttachmentFile) => Promise<void> | void;
  onDownload?: (attachment: AttachmentFile) => void;
};

type EditValues = Pick<AttachmentFile, "attachmentType" | "description" | "displayOrder">;

const fieldClassName = "min-h-9 w-full rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2.5 text-sm text-[var(--ots-text)] outline-none focus:border-[var(--ots-primary)] focus:ring-2 focus:ring-[var(--ots-primary-container)]";

/** Existing attachments with inline metadata editing and an explicit delete confirmation. */
export const AttachmentList = ({
  attachments,
  targets,
  capabilities,
  attachmentTypes,
  onUpdate,
  onDelete,
  onDownload,
}: AttachmentListProps) => {
  const [editingId, setEditingId] = useState<string>();
  const [deleteId, setDeleteId] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [values, setValues] = useState<EditValues>();
  const [actionError, setActionError] = useState<string>();

  const beginEdit = (attachment: AttachmentFile) => {
    setDeleteId(undefined);
    setActionError(undefined);
    setEditingId(attachment.id);
    setValues({
      attachmentType: attachment.attachmentType,
      description: attachment.description ?? "",
      displayOrder: attachment.displayOrder,
    });
  };

  const save = async (attachment: AttachmentFile) => {
    if (!values || !onUpdate) return;
    setBusyId(attachment.id);
    setActionError(undefined);
    try {
      await onUpdate(attachment, values);
      setEditingId(undefined);
      setValues(undefined);
    } catch (error) {
      setActionError(extractErrorMessage(error, "The attachment could not be updated. Please try again."));
    } finally {
      setBusyId(undefined);
    }
  };

  const remove = async (attachment: AttachmentFile) => {
    if (!onDelete) return;
    setBusyId(attachment.id);
    setActionError(undefined);
    try {
      await onDelete(attachment);
      setDeleteId(undefined);
    } catch (error) {
      setActionError(extractErrorMessage(error, "The attachment could not be deleted. Please try again."));
    } finally {
      setBusyId(undefined);
    }
  };

  if (!attachments.length) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--ots-outline-variant)] px-5 py-8 text-center">
        <DocumentIcon aria-hidden="true" className="mx-auto size-8 text-[var(--ots-text-muted)]"/>
        <p className="mt-2 text-sm font-semibold text-[var(--ots-secondary)]">No attachments yet</p>
        <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
          Files added here will be available anywhere this record is viewed.
        </p>
      </div>
    );
  }

  return (
    <section aria-labelledby="existing-attachments-heading" className="space-y-3">
      <h3 id="existing-attachments-heading" className="text-sm font-semibold text-[var(--ots-secondary)]">
        Attached files ({attachments.length})
      </h3>
      {actionError && <OtsInlineAlert tone="danger" title="Action failed" onDismiss={() => setActionError(undefined)}>{actionError}</OtsInlineAlert>}
      <ul className="divide-y divide-[var(--ots-outline-variant)] overflow-hidden rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]">
        {attachments.map((attachment) => {
          const editing = editingId === attachment.id;
          const confirmingDelete = deleteId === attachment.id;
          const busy = busyId === attachment.id;
          const target = targets.find((candidate) =>
            candidate.objectId === attachment.objectId && candidate.objectType === attachment.objectType);
          return (
            <li key={attachment.id} className="p-4">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
                  <DocumentIcon aria-hidden="true" className="size-5"/>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[var(--ots-text)]" title={attachment.fileName}>
                        {attachment.fileName}
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--ots-text-muted)]">
                        {attachmentLabel(attachment.attachmentType)} · {formatAttachmentSize(attachment.contentLength)}
                        {attachmentDate(attachment) ? ` · ${attachmentDate(attachment)}` : ""}
                      </p>
                      {targets.length > 1 && (
                        <p className="mt-1 text-xs font-medium text-[var(--ots-primary)]">
                          Attached to: {target?.label ?? `${attachment.objectType} ${attachment.objectId}`}
                        </p>
                      )}
                      {attachment.description && !editing && (
                        <p className="mt-2 text-sm text-[var(--ots-text-muted)]">{attachment.description}</p>
                      )}
                    </div>
                    {!editing && !confirmingDelete && (
                      <div className="flex shrink-0 items-center gap-1">
                        {attachment.downloadUrl && (
                          <a
                            href={attachment.downloadUrl}
                            download={attachment.fileName}
                            aria-label={`Download ${attachment.fileName}`}
                            title="Download"
                            onClick={() => onDownload?.(attachment)}
                            className="flex size-8 items-center justify-center rounded-lg text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
                            <ArrowDownTrayIcon aria-hidden="true" className="size-[18px]"/>
                          </a>
                        )}
                        {capabilities.canEdit && onUpdate && (
                          <OtsIconActionButton size="compact" variant="text" icon={<PencilSquareIcon/>} label={`Edit ${attachment.fileName}`} onClick={() => beginEdit(attachment)}/>
                        )}
                        {capabilities.canDelete && onDelete && (
                          <OtsIconActionButton size="compact" variant="text" icon={<TrashIcon/>} label={`Delete ${attachment.fileName}`} onClick={() => { setEditingId(undefined); setDeleteId(attachment.id); }}/>
                        )}
                      </div>
                    )}
                  </div>

                  {editing && values && (
                    <div className="mt-3 rounded-lg bg-[var(--ots-background)] p-3">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="text-xs font-medium text-[var(--ots-text-muted)]">
                          Category
                          <select
                            className={`${fieldClassName} mt-1`}
                            aria-label={`Category for ${attachment.fileName}`}
                            value={values.attachmentType}
                            disabled={busy}
                            onChange={(event) => setValues({...values, attachmentType: event.target.value as AttachmentTypeCode})}>
                            {!attachmentTypes.includes(values.attachmentType) && (
                              <option value={values.attachmentType}>{attachmentLabel(values.attachmentType)}</option>
                            )}
                            {attachmentTypes.map((type) => <option key={type} value={type}>{attachmentLabel(type)}</option>)}
                          </select>
                        </label>
                        <label className="text-xs font-medium text-[var(--ots-text-muted)]">
                          Description
                          <input
                            className={`${fieldClassName} mt-1`}
                            aria-label={`Description for ${attachment.fileName}`}
                            value={values.description ?? ""}
                            disabled={busy}
                            maxLength={500}
                            onChange={(event) => setValues({...values, description: event.target.value})}/>
                        </label>
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        <OtsActionButton size="compact" variant="text" disabled={busy} onClick={() => setEditingId(undefined)}>Cancel</OtsActionButton>
                        <OtsAsyncActionButton size="compact" variant="primary" isLoading={busy} loadingLabel="Saving…" onClick={() => save(attachment)}>Save changes</OtsAsyncActionButton>
                      </div>
                    </div>
                  )}

                  {confirmingDelete && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] p-3">
                      <p className="text-sm font-medium text-[var(--ots-on-danger-container)]">Delete this attachment? This cannot be undone.</p>
                      <div className="flex gap-2">
                        <OtsIconActionButton size="compact" variant="text" icon={<XMarkIcon/>} label="Cancel delete" disabled={busy} onClick={() => setDeleteId(undefined)}/>
                        <OtsAsyncActionButton size="compact" variant="destructive" isLoading={busy} loadingLabel="Deleting…" onClick={() => remove(attachment)}>Confirm delete</OtsAsyncActionButton>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
